<template>
  <div>
    <canvas ref="lineChartCanvas"></canvas>
  </div>
</template>

<script>
export default {
  name: "LineChart",
};
</script>
<script setup>
import { ref, onMounted, onUnmounted, defineProps } from "vue";
import {
  LineElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Chart from "chart.js/auto";

// Register components
Chart.register(LineElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const props = defineProps({
  finances: Object,
});

const lineChartCanvas = ref(null);
let chartInstance = null;

// Короткий формат чисел: 12_300 -> "12,3 тыс", 2_450_000 -> "2,45 млн" и т.д.
const formatShort = (n) => {
  const abs = Math.abs(n);
  if (abs >= 1e12) return (n / 1e12).toFixed(0) + " трлн";
  if (abs >= 1e9) return (n / 1e9).toFixed(0) + " млрд";
  if (abs >= 1e6) return (n / 1e6).toFixed(0) + " млн";
  if (abs >= 1e3) return (n / 1e3).toFixed(0) + " тыс";
  return n.toFixed(1);
};

// округлим верхнюю границу до «красивого» шага
function niceCeil(max) {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const base = Math.ceil(max / pow);
  const niceBase = [1, 2, 5, 10].find((v) => v >= base) ?? 10;
  return niceBase * pow;
}

const initChart = () => {
  if (chartInstance) {
    chartInstance.destroy();
  }

  if (lineChartCanvas.value) {
    const ctx = lineChartCanvas.value?.getContext("2d");
    if (!ctx) return;

    const labels = props.finances.finances_years.map((item) => item.year);
    // const data = props.finances.finances_years?.map((item) =>
    //   item.amount.reduce((sum, num) => sum + parseFloat(num) / 1000, 0)
    // );
    // суммируем amount[] по каждому году
    const data = props.finances.finances_years.map((item) =>
      (item.amount ?? []).reduce((sum, num) => sum + Number(num || 0), 0)
    );

    const maxVal = Math.max(...data); // реальный максимум из данных
    const yMax = +maxVal.toFixed(1); // округлим максимум до десятых
    const mid = +(yMax / 2).toFixed(1); // половина — тоже до десятых

    chartInstance = new Chart(ctx, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Количество",
            data: data,
            borderColor: "#42A5F5",
            backgroundColor: "rgba(66, 165, 245, 0.2)",
            borderWidth: 2,
            // fill: true,
            tension: 0.4,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              // label: function (context) {
              //   return `${context.dataset.label}: ${context.raw}`;
              // },
              label: (ctx) =>
                `${ctx.dataset.label}: ${formatShort(Number(ctx.raw))}`,
            },
          },
        },
        scales: {
          x: {
            title: {
              display: false,
            },
            grid: { display: false, drawTicks: false },
            border: { display: false },
          },
          // y: {
          //   title: {
          //     display: false,
          //   },
          //   ticks: {
          //     display: false,
          //   },
          // },

          y: {
            position: "right",
            min: 0,
            // max: yMax, // верх = реальный максимум (до десятых)
            max: Math.max(yMax, 1e-9),
            bounds: "ticks", // тики = реальные границы
            grace: 0,
            grid: { display: true, drawTicks: false, drawBorder: false },
            ticks: {
              autoSkip: false, // не даём Chart.js пропускать тики
              callback: (val) => formatShort(Number(val)), // формат с 1 знаком
            },
            // 👉 ЖЁСТКО задаём ровно три тика: 0, mid, max
            afterBuildTicks: (scale) => {
              scale.ticks = [0, mid, yMax].map((v) => ({ value: v }));
            },
          },
          r: {
            ticks: {
              callback: function () {
                return null;
              },
            },
          },
        },
      },
    });
  }
};

const checkVisibility = (entries) => {
  if (entries[0]?.isIntersecting) {
    initChart();
  }
};

onMounted(() => {
  const observer = new IntersectionObserver(checkVisibility, {
    threshold: 0.1,
  });
  if (lineChartCanvas.value) {
    observer.observe(lineChartCanvas.value);
  }

  onUnmounted(() => {
    if (lineChartCanvas.value) {
      if (lineChartCanvas.value && lineChartCanvas.value instanceof Element) {
        observer.unobserve(lineChartCanvas.value);
      }
    }
    if (chartInstance) {
      chartInstance.destroy();
    }
  });
});
</script>
